import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CaretDown, CaretUp, ArrowsClockwise, CheckCircle, XCircle,
  Microphone, Play, Image as ImageIcon, Trash, ArrowSquareOut, DownloadSimple, Plus, Copy, Tag,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "../../lib/api";

function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return iso; }
}

function StatusPill({ status }) {
  const map = {
    pending: "text-amber-300 bg-amber-500/10 border-amber-400/30",
    promoted: "text-emerald-400 bg-emerald-500/10 border-emerald-400/30",
    rejected: "text-zinc-500 bg-ink-800 border-ink-600",
  };
  return (
    <span className={`text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-0.5 rounded border ${map[status] || map.pending}`}>
      {status}
    </span>
  );
}

function PromoteMenu({ onPromote, onClose, align = "right" }) {
  return (
    <div
      className={`absolute ${align === "right" ? "right-0" : "left-0"} top-full mt-2 z-30 rounded-lg border border-ink-600 bg-ink-800 shadow-xl p-2 grid grid-cols-5 gap-1`}
      onClick={(e) => e.stopPropagation()}
      data-testid="promote-menu"
    >
      <div className="col-span-5 px-1 pb-1 mb-0.5 text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500 border-b border-ink-700">
        Add to slot
      </div>
      {Array.from({ length: 10 }, (_, i) => i + 1).map((r) => (
        <button
          key={r}
          onClick={() => { onPromote(r); onClose(); }}
          className="px-2 py-1.5 rounded text-xs font-mono text-zinc-200 hover:bg-neon-cyan hover:text-black font-semibold transition-colors"
          data-testid={`promote-to-${r}`}
        >
          #{String(r).padStart(2, "0")}
        </button>
      ))}
    </div>
  );
}

function safeDownloadName(item, kind) {
  const artist = (item.artist_name || "artist").replace(/[^\w.\- ]+/g, "").trim() || "artist";
  const song = (item.song_title || "track").replace(/[^\w.\- ]+/g, "").trim() || "track";
  const src = kind === "audio" ? item.audio_path : item.image_path;
  const ext = (src || "").split(".").pop().split("?")[0].toLowerCase();
  const safeExt = /^[a-z0-9]{2,5}$/.test(ext) ? ext : (kind === "audio" ? "mp3" : "png");
  return `${artist} - ${song} - ${kind}.${safeExt}`;
}

function downloadUrl(path, name) {
  const base = fileUrl(path);
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}download=1&name=${encodeURIComponent(name)}`;
}

function Row({ item, expanded, onToggle, onPromote, onReject, onDelete, onPreview }) {
  const [menu, setMenu] = useState(false);
  const [quickMenu, setQuickMenu] = useState(false);

  const copyIsrc = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(item.isrc || "");
      toast.success("ISRC copied");
    } catch { toast.error("Couldn't copy"); }
  };

  return (
    <li className="border-b border-ink-700/60 last:border-0" data-testid={`submission-row-${item.id}`}>
      <div className="w-full flex items-center gap-3 px-4 py-3 hover:bg-ink-800/40 transition-colors">
        <button
          onClick={onToggle}
          className="flex-1 min-w-0 flex items-center gap-3 text-left"
          data-testid={`submission-toggle-${item.id}`}
        >
          <Microphone size={14} className="text-neon-cyan shrink-0" />
          <div className="flex-1 min-w-0 grid grid-cols-12 gap-3 items-center">
            <div className="col-span-12 sm:col-span-4 truncate text-sm text-white font-semibold">{item.artist_name}</div>
            <div className="col-span-8 sm:col-span-4 truncate text-xs text-zinc-300 font-mono">{item.song_title}</div>
            <div className="hidden sm:flex sm:col-span-2 justify-start"><StatusPill status={item.status} /></div>
            <div className="col-span-4 sm:col-span-2 text-right text-[10px] font-mono text-zinc-500">{timeAgo(item.created_at)}</div>
          </div>
        </button>
        {item.status !== "promoted" && (
          <div className="relative shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); setQuickMenu((v) => !v); setMenu(false); }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-neon-cyan text-black text-[11px] font-semibold hover:bg-neon-cyanHover shadow-[0_0_16px_rgba(0,240,255,0.25)]"
              title="One-click add to Top 10"
              data-testid={`submission-quick-add-${item.id}`}
            >
              <Plus size={13} weight="bold" />
              <span className="hidden sm:inline">Add to Top 10</span>
              <span className="sm:hidden">Add</span>
            </button>
            {quickMenu && <PromoteMenu align="right" onPromote={(r) => onPromote(r)} onClose={() => setQuickMenu(false)} />}
          </div>
        )}
        {item.status === "promoted" && item.promoted_to_rank && (
          <span className="text-[10px] font-mono text-emerald-400 shrink-0 inline-flex items-center gap-1">
            <CheckCircle size={12} weight="fill" /> #{String(item.promoted_to_rank).padStart(2, "0")}
          </span>
        )}
        <button onClick={onToggle} className="shrink-0 text-zinc-500 p-1" aria-label={expanded ? "Collapse" : "Expand"}>
          {expanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
        </button>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3" data-testid={`submission-detail-${item.id}`}>
          {item.isrc && (
            <div className="inline-flex items-center gap-2 rounded-md border border-neon-cyan/30 bg-neon-cyan/5 px-2.5 py-1 text-[11px] font-mono text-neon-cyan" data-testid={`submission-isrc-${item.id}`}>
              <Tag size={12} weight="fill" />
              <span className="tracking-widest">{item.isrc}</span>
              <button onClick={copyIsrc} className="text-zinc-400 hover:text-neon-cyan" title="Copy ISRC" data-testid={`submission-isrc-copy-${item.id}`}>
                <Copy size={11} />
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              {item.image_path ? (
                <img src={fileUrl(item.image_path)} alt="" className="rounded-md w-full aspect-square object-cover border border-ink-600" />
              ) : (
                <div className="rounded-md w-full aspect-square border border-dashed border-ink-600 flex items-center justify-center text-zinc-600">
                  <ImageIcon size={28} />
                </div>
              )}
              {item.audio_path && (
                <>
                  <button onClick={() => onPreview(item)} className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded border border-ink-600 text-zinc-200 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs" data-testid={`submission-preview-${item.id}`}>
                    <Play size={14} weight="fill" /> Preview audio
                  </button>
                  <a
                    href={downloadUrl(item.audio_path, safeDownloadName(item, "audio"))}
                    download={safeDownloadName(item, "audio")}
                    className="mt-1.5 w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded border border-ink-600 text-zinc-200 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs"
                    data-testid={`submission-download-audio-${item.id}`}
                    title={`Download ${item.audio_path.split('/').pop()}`}
                  >
                    <DownloadSimple size={14} weight="bold" /> Download audio
                  </a>
                </>
              )}
              {item.image_path && (
                <a
                  href={downloadUrl(item.image_path, safeDownloadName(item, "image"))}
                  download={safeDownloadName(item, "image")}
                  className="mt-1.5 w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded border border-ink-600 text-zinc-200 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs"
                  data-testid={`submission-download-image-${item.id}`}
                >
                  <DownloadSimple size={14} weight="bold" /> Download artwork
                </a>
              )}
            </div>
            <div className="sm:col-span-2 space-y-3 text-sm">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Contact</div>
                <a href={`mailto:${item.contact_email}`} className="text-zinc-200 hover:text-neon-cyan font-mono text-xs">{item.contact_email}</a>
              </div>
              {item.bio && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-1">Bio</div>
                  <p className="text-zinc-300 text-sm whitespace-pre-wrap leading-relaxed">{item.bio}</p>
                </div>
              )}
              {item.social_links?.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-1">Links</div>
                  <div className="flex flex-wrap gap-1.5">
                    {item.social_links.map((s) => (
                      <a key={`${s.platform}-${s.url}`} href={s.url.startsWith("http") ? s.url : `https://${s.url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-ink-600 text-xs text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50">
                        {s.platform} <ArrowSquareOut size={10} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {item.video_url && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500 mb-1">Video</div>
                  <a href={item.video_url} target="_blank" rel="noreferrer" className="text-neon-cyan text-xs font-mono break-all">{item.video_url}</a>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-ink-700/60">
            {item.status !== "promoted" && (
              <div className="relative">
                <button onClick={(e) => { e.stopPropagation(); setMenu((v) => !v); setQuickMenu(false); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-neon-cyan/40 text-neon-cyan text-xs font-semibold hover:bg-neon-cyan/10" data-testid={`submission-promote-${item.id}`}>
                  <CheckCircle size={14} weight="fill" /> Add to Top 10
                </button>
                {menu && <PromoteMenu align="left" onPromote={(r) => onPromote(r)} onClose={() => setMenu(false)} />}
              </div>
            )}
            {item.status === "promoted" && item.promoted_to_rank && (
              <span className="text-xs text-emerald-400 inline-flex items-center gap-1.5 font-mono">
                <CheckCircle size={14} weight="fill" /> Promoted to #{String(item.promoted_to_rank).padStart(2, "0")}
              </span>
            )}
            {item.status !== "rejected" && (
              <button onClick={onReject} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-amber-300 hover:border-amber-300/40 text-xs" data-testid={`submission-reject-${item.id}`}>
                <XCircle size={14} /> Reject
              </button>
            )}
            <button onClick={onDelete} className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-magenta hover:border-neon-magenta/50 text-xs" data-testid={`submission-delete-${item.id}`}>
              <Trash size={14} /> Delete
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function SubmissionsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [openId, setOpenId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/submissions");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((s) => s.status === filter);
  }, [items, filter]);

  const counts = useMemo(() => ({
    all: items.length,
    pending: items.filter((s) => s.status === "pending").length,
    promoted: items.filter((s) => s.status === "promoted").length,
    rejected: items.filter((s) => s.status === "rejected").length,
  }), [items]);

  const promote = async (id, rank) => {
    try {
      const { data } = await api.post(`/submissions/${id}/promote`, { rank });
      setItems((prev) => prev.map((s) => s.id === id ? { ...s, status: "promoted", promoted_to_rank: data.promoted_to_rank } : s));
      toast.success(`Promoted to #${String(rank).padStart(2, "0")}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const reject = async (id) => {
    try {
      const { data } = await api.patch(`/submissions/${id}`, { status: "rejected" });
      setItems((prev) => prev.map((s) => s.id === id ? data : s));
      toast.success("Submission rejected");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await api.delete(`/submissions/${id}`);
      setItems((prev) => prev.filter((s) => s.id !== id));
      if (openId === id) setOpenId(null);
      toast.success("Submission deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const preview = (item) => {
    if (!item.audio_path) return;
    setPreviewUrl(fileUrl(item.audio_path));
  };

  return (
    <section data-testid="admin-submissions">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs font-bold tracking-[0.3em] text-neon-cyan uppercase mb-2">Submissions</p>
          <h2 className="text-2xl sm:text-3xl font-black tracking-tighter">
            Artist pile <span className="text-zinc-500 font-mono text-base">({counts.pending} pending)</span>
          </h2>
          <p className="text-sm text-zinc-500 mt-2">Pick a heater, hit Promote, choose a slot. The lineup updates instantly.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-ink-600 overflow-hidden text-xs">
            {[
              { k: "pending", label: `Pending ${counts.pending}` },
              { k: "promoted", label: `Promoted ${counts.promoted}` },
              { k: "rejected", label: `Rejected ${counts.rejected}` },
              { k: "all", label: `All ${counts.all}` },
            ].map((t) => (
              <button key={t.k} onClick={() => setFilter(t.k)} className={`px-2.5 py-1.5 ${filter === t.k ? "bg-neon-cyan text-black font-semibold" : "text-zinc-400 hover:text-white"}`} data-testid={`submissions-filter-${t.k}`}>
                {t.label}
              </button>
            ))}
          </div>
          <button onClick={load} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-ink-600 text-zinc-300 hover:text-neon-cyan hover:border-neon-cyan/50 text-xs" data-testid="submissions-reload"><ArrowsClockwise size={14} /> Reload</button>
        </div>
      </div>

      <div className="rounded-xl border border-ink-600 bg-ink-900/40 overflow-hidden">
        {loading ? (
          <div className="px-4 py-6 text-zinc-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-12 text-center text-zinc-500 text-sm" data-testid="submissions-empty">
            {items.length === 0
              ? "No artist submissions yet. Share the /submit link on the show — they'll appear here."
              : "No submissions match this filter."}
          </div>
        ) : (
          <ul>
            {filtered.map((item) => (
              <Row
                key={item.id}
                item={item}
                expanded={openId === item.id}
                onToggle={() => setOpenId((cur) => cur === item.id ? null : item.id)}
                onPromote={(rank) => promote(item.id, rank)}
                onReject={() => reject(item.id)}
                onDelete={() => remove(item.id)}
                onPreview={preview}
              />
            ))}
          </ul>
        )}
      </div>

      {previewUrl && (
        <div className="fixed bottom-6 right-6 z-50 rounded-lg border border-ink-600 bg-ink-800 shadow-xl p-3 flex items-center gap-3 max-w-md" data-testid="submission-preview-player">
          <Microphone size={16} className="text-neon-cyan shrink-0" />
          <audio src={previewUrl} controls autoPlay className="flex-1 max-w-xs" data-testid="submission-preview-audio" />
          <button onClick={() => setPreviewUrl("")} className="text-zinc-500 hover:text-neon-magenta" data-testid="submission-preview-close" aria-label="Close preview">
            <XCircle size={18} weight="bold" />
          </button>
        </div>
      )}
    </section>
  );
}
